/**
 * SidebarCanvas - Center panel of the sidebar builder
 *
 * Shows the current sidebar layout as draggable categories with
 * sortable items inside. Users can reorder categories, reorder items,
 * move items between categories, rename categories, and remove items.
 */

import React, { useState } from 'react';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical, X, ChevronDown, ChevronRight, Plus, Edit2, Check, Box
} from 'lucide-react';
import { MODULE_ICONS } from '../../../constants/profile_constants.js';
import { generateCategoryId } from './builderUtils.js';

// Sortable item within a category
function SortableItem({ id, label, onRemove, allFeatures }) {
  const featureData = allFeatures.find(f => f.id === id) || { id, label: label || id };
  const Icon = MODULE_ICONS[id] || Box;

  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({
    id: `item-${id}`,
    data: {
      type: 'canvas-item',
      featureId: id,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg group transition-colors
        ${isDragging
          ? 'opacity-50 bg-blue-500/20 ring-1 ring-blue-500'
          : 'bg-white dark:bg-slate-700/60 hover:bg-gray-50 dark:hover:bg-slate-600/60 border border-gray-200 dark:border-slate-600'
        }`}
    >
      <button
        {...attributes}
        {...listeners}
        className="text-gray-400 dark:text-slate-500 cursor-grab active:cursor-grabbing flex-shrink-0"
      >
        <GripVertical size={14} />
      </button>
      <Icon size={14} className="text-gray-500 dark:text-slate-400 flex-shrink-0" />
      <span className="text-sm text-gray-700 dark:text-slate-300 truncate flex-1">
        {featureData.label}
      </span>
      <button
        onClick={() => onRemove(id)}
        className="p-0.5 rounded text-gray-300 dark:text-slate-600 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
        title="Retirer du groupe"
      >
        <X size={12} />
      </button>
    </div>
  );
}

// Droppable category with sortable items
function Category({ category, index, allFeatures, onRemoveItem, onRenameCategory, onDeleteCategory, autoEditNew }) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isEditing, setIsEditing] = useState(autoEditNew || false);
  const [editLabel, setEditLabel] = useState(category.label);

  const {
    attributes, listeners, setNodeRef: setSortableRef, transform, transition, isDragging,
  } = useSortable({
    id: category.id,
    data: { type: 'category', categoryIndex: index },
  });

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `drop-${category.id}`,
    data: {
      type: 'category-drop',
      categoryId: category.id,
    },
  });

  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleRename = () => {
    if (editLabel.trim() && editLabel !== category.label) {
      onRenameCategory(category.id, editLabel.trim());
    }
    setIsEditing(false);
  };

  const sortableItemIds = category.items.map(id => `item-${id}`);

  return (
    <div
      ref={setSortableRef}
      style={sortableStyle}
      className={`rounded-xl border transition-all
        ${isDragging ? 'opacity-50 ring-2 ring-blue-500' : ''}
        ${isOver ? 'ring-2 ring-blue-400 border-blue-400' : 'border-gray-200 dark:border-slate-700'}
        bg-gray-50 dark:bg-slate-800/50
      `}
    >
      {/* Category header */}
      <div className="group flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-slate-700">
        <button
          {...attributes}
          {...listeners}
          className="text-gray-400 dark:text-slate-500 cursor-grab active:cursor-grabbing"
        >
          <GripVertical size={16} />
        </button>

        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="text-gray-400 dark:text-slate-500"
        >
          {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
        </button>

        {isEditing ? (
          <div className="flex items-center gap-1 flex-1">
            <input
              type="text"
              value={editLabel}
              onChange={e => setEditLabel(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setIsEditing(false); }}
              autoFocus
              className="flex-1 px-2 py-0.5 text-sm bg-white dark:bg-slate-700 border border-blue-500 rounded text-gray-900 dark:text-white focus:outline-none"
            />
            <button onClick={handleRename} className="p-0.5 text-green-500 hover:text-green-400">
              <Check size={14} />
            </button>
          </div>
        ) : (
          <div className="flex-1 flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-700 dark:text-slate-300">
              {category.label}
            </span>
            <span className="text-[10px] text-gray-400 dark:text-slate-500 bg-gray-200 dark:bg-slate-700 px-1.5 py-0.5 rounded">
              {category.items.length}
            </span>
            <button
              onClick={() => { setEditLabel(category.label); setIsEditing(true); }}
              className="p-0.5 text-gray-300 dark:text-slate-600 hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Renommer"
            >
              <Edit2 size={12} />
            </button>
          </div>
        )}

        {category.items.length === 0 && (
          <button
            onClick={() => onDeleteCategory(category.id)}
            className="p-1 text-gray-400 dark:text-slate-500 hover:text-red-500 transition-colors"
            title="Supprimer la catégorie vide"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Category items */}
      {!isCollapsed && (
        <div
          ref={setDropRef}
          className={`p-2 space-y-1 min-h-[40px] transition-colors
            ${isOver ? 'bg-blue-500/5' : ''}
          `}
        >
          <SortableContext items={sortableItemIds} strategy={verticalListSortingStrategy}>
            {category.items.map(itemId => (
              <SortableItem
                key={itemId}
                id={itemId}
                allFeatures={allFeatures}
                onRemove={onRemoveItem}
              />
            ))}
          </SortableContext>

          {category.items.length === 0 && (
            <div className="text-center py-4 text-xs text-gray-400 dark:text-slate-500 border-2 border-dashed border-gray-200 dark:border-slate-600 rounded-lg">
              Déposez des fonctionnalités ici
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function SidebarCanvas({ state, allFeatures, onRemoveItem, onRenameCategory, onDeleteCategory, onAddCategory }) {
  const [newCatId, setNewCatId] = useState(null);
  const categoryIds = state.categories.map(c => c.id);

  const handleAddCategory = () => {
    onAddCategory();
    // Mark the next new category for auto-edit (will be the last one after state update)
    setNewCatId('pending');
  };

  // Detect when the new category appears in state
  React.useEffect(() => {
    if (newCatId === 'pending' && state.categories.length > 0) {
      const lastCat = state.categories[state.categories.length - 1];
      if (lastCat.id.startsWith('cat-custom-')) {
        setNewCatId(lastCat.id);
      }
    }
  }, [state.categories, newCatId]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-3 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-300">
            Structure de la sidebar
          </h3>
          <div className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5">
            {state.categories.length} groupe{state.categories.length !== 1 ? 's' : ''} — glisser pour réorganiser
          </div>
        </div>
        <button
          onClick={handleAddCategory}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          <Plus size={14} />
          Groupe
        </button>
      </div>

      {/* Canvas content */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        <SortableContext items={categoryIds} strategy={verticalListSortingStrategy}>
          {state.categories.map((cat, idx) => (
            <Category
              key={cat.id}
              category={cat}
              index={idx}
              allFeatures={allFeatures}
              onRemoveItem={onRemoveItem}
              onRenameCategory={(id, label) => { onRenameCategory(id, label); setNewCatId(null); }}
              onDeleteCategory={onDeleteCategory}
              autoEditNew={cat.id === newCatId}
            />
          ))}
        </SortableContext>

        {state.categories.length === 0 && (
          <div className="text-center py-12 text-sm text-gray-400 dark:text-slate-500">
            Ajoutez un groupe pour commencer
          </div>
        )}
      </div>
    </div>
  );
}

export default SidebarCanvas;
